// typings
import { Exchange } from 'modules/Search/constants';
// service
import ScripsDb from 'modules/Db';
import Logger from 'modules/Logger';
import Search from '../../Search';

export default class BodParser extends Search {
  private flattenAndSplitScripsToRows(scrips: Scrips | null): ScripDbRow[][] {
    if (!scrips) return [];

    const BATCH_SIZE = 5000;
    const batches: ScripDbRow[][] = [[]];
    let currentBatch = 0;

    Object.entries(scrips).forEach(([, segments]) => {
      Object.entries(segments).forEach(([, tokenObjects]) => {
        tokenObjects.forEach(tokenObject => {
          const token = tokenObject?.instrumentKey?.split('|')?.[1];

          if (token === '0') {
            return;
          }

          const row: ScripDbRow = {
            data: tokenObject,
            id: `${tokenObject.instrumentKey}` || '',
          };

          // If current batch is full, create new batch
          if (batches[currentBatch].length >= BATCH_SIZE) {
            currentBatch++;
            batches[currentBatch] = [];
          }

          batches[currentBatch].push(row);
        });
      });
    });

    return batches;
  }

  //  get database names
  private async getExitingDatabaseNames() {
    const existingDbs = await ScripsDb.getDatabaseNames();
    return existingDbs;
  }

  // parse method
  public parseData(BOD: ScripFile) {
    // Clear memory
    this.clear();
    // Parse indices
    this.parseIndices(BOD.bse.i, Exchange.BSE, BOD.bse.to.TOKENS.BSE_INDICES);
    this.parseIndices(BOD.nse.i, Exchange.NSE, BOD.nse.to.TOKENS.NSE_INDICES);
    this.parseIndices(BOD.mcx.i, Exchange.MCX);

    // Parse equities
    this.parseEquities(BOD.bse.eq, Exchange.BSE);
    this.parseEquities(BOD.nse.eq, Exchange.NSE);

    // Parse futures and options
    this.parseFutures(BOD.nse.f, Exchange.NSE);
    this.parseFutures(BOD.bse.f, Exchange.BSE);
    if (BOD.nse.wf) this.parseFutures(BOD.nse.wf, Exchange.NSE, true);
    this.parseOptions(BOD.nse.o, Exchange.NSE);
    this.parseOptions(BOD.nse.wo, Exchange.NSE, true);
    this.parseOptions(BOD.bse.o, Exchange.BSE);
    this.parseOptions(BOD.bse.wo, Exchange.BSE, true);

    // Parse currency futures and options
    this.parseCurrencyFutures(
      BOD.bse.cf,
      Exchange.BSE,
      BOD.bse.to.TOKENS.BCD_FO,
    );
    this.parseCurrencyOptions(
      BOD.bse.co,
      Exchange.BSE,
      BOD.bse.to.TOKENS.BCD_FO,
    );
    this.parseCurrencyFutures(BOD.nse.cf, Exchange.NSE, BOD.nse.to.TOKENS.NCD);
    this.parseCurrencyOptions(BOD.nse.co, Exchange.NSE, BOD.nse.to.TOKENS.NCD);
    this.parseCurrencyOptions(
      BOD.nse.wco,
      Exchange.NSE,
      BOD.nse.to.TOKENS.NCD,
      true,
    );

    // Parse commodity futures and options
    this.parseCurrencyFutures(
      BOD.mcx.f,
      Exchange.MCX,
      BOD.mcx.to.TOKENS.MCX_FO,
    );
    this.parseCurrencyOptions(
      BOD.mcx.o,
      Exchange.MCX,
      BOD.mcx.to.TOKENS.MCX_FO,
    );
    this.parseCurrencyFutures(
      BOD.nse.comf,
      Exchange.NSE,
      BOD.nse.to.TOKENS.NSE_COM,
    );
    this.parseCurrencyOptions(
      BOD.nse.como,
      Exchange.NSE,
      BOD.nse.to.TOKENS.NSE_COM,
    );

    Logger.log('BOD', 'BOD parse is complete-->');
  }

  public async insertDataInDb() {
    try {
      const startTime = Date.now();

      Logger.log('BOD', 'DB insertion started...');

      // Await database operations
      const existingDbs = await this.getExitingDatabaseNames();
      const batches = this.flattenAndSplitScripsToRows(this.scrips);

      await Promise.all([
        ScripsDb.clearOldDatabase(existingDbs),
        ScripsDb.recreateDatabase(existingDbs),
      ]);

      await ScripsDb.updateMetadata('dbWriteStatus', 'in-progress');

      // Process batches sequentially
      // eslint-disable-next-line no-restricted-syntax
      for await (const batch of batches) {
        await ScripsDb.bulkPutScrips(batch);
      }

      await ScripsDb.updateMetadata('dbWriteStatus', 'completed');

      const endTime = Date.now();

      const loadTimeInSeconds = ((endTime - startTime) / 1000).toFixed(2);

      Logger.log(
        'BOD',
        `Performance: total load time ${loadTimeInSeconds} seconds`,
      );

      this.scrips = null;
    } catch (error) {
      ScripsDb.updateMetadata('dbWriteStatus', 'failed');
      // Handle errors
      Logger.error(error);
      throw error; // Ensure the caller knows about the failure
    }
  }
}
